/**
 * User Activity Logs View (FP-6471)
 *
 * Renders the end-user's journey across the consent flow as a timeline.
 * Groups events by attempt_id — a single consent may have multiple retry attempts.
 * Each attempt is collapsible. Inside each attempt, events use the shared
 * Equal DS Timeline component for visual parity with Consent Logs and Data Logs.
 */

import React, { useMemo, useState } from 'react';
import { Timeline, Loader } from 'equal-ds-ui';
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  LogOut,
  XCircle,
} from 'lucide-react';
import type {
  ActivityLogAttempt,
  ActivityLogEntry,
  ActivityLogTimeline,
  TimelineEvent,
} from '../../../types/timeline';

interface UserActivityLogsViewProps {
  logs: ActivityLogTimeline | null;
  isLoading?: boolean;
  error?: string | null;
}

const OUTCOME_META: Record<
  ActivityLogAttempt['outcome'],
  { label: string; colorClass: string; icon: React.ComponentType<{ className?: string }> }
> = {
  APPROVED: {
    label: 'Approved',
    colorClass: 'bg-green-100 text-green-700 border-green-200',
    icon: CheckCircle2,
  },
  REJECTED: {
    label: 'Rejected',
    colorClass: 'bg-red-100 text-red-700 border-red-200',
    icon: XCircle,
  },
  CANCELLED: {
    label: 'Cancelled',
    colorClass: 'bg-orange-100 text-orange-700 border-orange-200',
    icon: LogOut,
  },
  DROPPED: {
    label: 'Dropped',
    colorClass: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    icon: AlertCircle,
  },
  IN_PROGRESS: {
    label: 'In Progress',
    colorClass: 'bg-blue-100 text-blue-700 border-blue-200',
    icon: Clock,
  },
};

/**
 * Equal-ds Timeline `status.type` for every activity-log row.
 *
 * Product spec (FP-6471, May 2026): every event in the timeline shows a
 * green success dot — EXCEPT system/FIP failure events, which show a red
 * error dot. User-initiated rejections and cancellations are NOT failures
 * (they're valid outcomes), so they stay green.
 *
 * Failure detection is token-based on the raw event_code (which still
 * carries its AA prefix here, e.g. `OM-CONSENT-FAILED`), so we don't need
 * to strip the prefix client-side.
 *
 * Chip text is suppressed below in toTimelineEvent — the dot color alone
 * marks the failure; the label conveys the semantic meaning.
 */
const FAILURE_TOKENS = ['FAILED', 'ERROR', 'LOCKED', 'INVALID', 'UNAVAILABLE', 'EXPIRED'];
function deriveStatusType(code: string): TimelineEvent['status']['type'] {
  const upper = (code ?? '').toUpperCase();
  if (FAILURE_TOKENS.some(token => upper.includes(token))) {
    return 'error';
  }
  return 'success';
}

/** Format an ISO timestamp the same way transformConsentTimeline does. */
function formatTimestamp(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const timeString = d.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
  const dateString = d.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
  });
  return `${timeString}, ${dateString}`;
}

function formatDuration(startIso: string, endIso: string): string {
  const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
  if (!Number.isFinite(ms) || ms <= 0) return '';
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  return `${min}m ${rem}s`;
}

/** Convert an ActivityLogEntry into the TimelineEvent shape Timeline expects. */
function toTimelineEvent(e: ActivityLogEntry, idx: number): TimelineEvent {
  const description = e.message && e.message !== e.label ? e.message : undefined;
  return {
    id: `${e.attempt_id}-${idx}-${e.event_code}`,
    timestamp: formatTimestamp(e.timestamp),
    // FP-6471 product spec: uniform green dot, no chip text. The empty
    // label suppresses the equal-ds status pill while keeping the dot.
    status: {
      type: deriveStatusType(e.event_code),
      label: '',
    },
    title: e.label,
    description,
    metadata: {
      event_code: e.event_code,
    },
  };
}

const AttemptSection: React.FC<{
  attempt: ActivityLogAttempt;
  defaultOpen: boolean;
}> = ({ attempt, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  const meta = OUTCOME_META[attempt.outcome];
  const Icon = meta.icon;
  const duration = formatDuration(attempt.start_time, attempt.end_time);

  // Events already newest-first in the attempt; render ascending for story flow
  const timelineItems = useMemo(
    () => [...attempt.events].reverse().map(toTimelineEvent),
    [attempt.events]
  );

  return (
    <div className="border border-border-light rounded-xl overflow-hidden bg-background-secondary">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-background-muted transition-colors"
      >
        <div className="flex items-center gap-3">
          {open ? (
            <ChevronDown className="w-4 h-4 text-text-tertiary" />
          ) : (
            <ChevronRight className="w-4 h-4 text-text-tertiary" />
          )}
          <span className="text-sm font-semibold text-text-primary">
            Attempt {attempt.attempt_number}
          </span>
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${meta.colorClass}`}
          >
            <Icon className="w-3 h-3" />
            {meta.label}
          </span>
          {attempt.exit_reason && (
            <span className="text-xs text-text-tertiary italic truncate max-w-xs">
              reason: {attempt.exit_reason}
            </span>
          )}
        </div>
        <div className="flex items-center gap-4 text-xs text-text-muted">
          <span>
            {attempt.events.length} {attempt.events.length === 1 ? 'event' : 'events'}
          </span>
          {duration && (
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {duration}
            </span>
          )}
          <span>{formatTimestamp(attempt.start_time)}</span>
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4 pt-2">
          <Timeline
            items={timelineItems}
            variant="default"
            showConnectors={true}
            connectorGap={2}
            className="!justify-start !h-auto gap-6"
          />
        </div>
      )}
    </div>
  );
};

export const UserActivityLogsView: React.FC<UserActivityLogsViewProps> = ({
  logs,
  isLoading,
  error,
}) => {
  const summary = useMemo(() => {
    if (!logs) return null;
    const approved = logs.attempts.filter(a => a.outcome === 'APPROVED').length;
    const cancelled = logs.attempts.filter(a => a.outcome === 'CANCELLED').length;
    const rejected = logs.attempts.filter(a => a.outcome === 'REJECTED').length;
    const dropped = logs.attempts.filter(a => a.outcome === 'DROPPED').length;
    return {
      approved,
      cancelled,
      rejected,
      dropped,
      total: logs.attempts.length,
    };
  }, [logs]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader size={64} cellSize={16} />
        <p className="text-sm text-text-tertiary mt-4">Loading activity logs…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mb-3" />
        <p className="text-sm font-medium text-text-primary">Unable to load activity logs</p>
        <p className="text-xs text-text-tertiary mt-1 max-w-sm">{error}</p>
      </div>
    );
  }

  if (!logs || logs.attempts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
        <Clock className="w-12 h-12 text-text-tertiary mb-3" />
        <p className="text-sm font-medium text-text-primary">No activity logs available</p>
        <p className="text-xs text-text-tertiary mt-1 max-w-sm">
          The user journey for this consent has no captured events yet.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full space-y-3">
      {/* Summary header */}
      <div className="flex items-center justify-between flex-shrink-0">
        <h3 className="text-sm font-medium text-text-primary tracking-wider">USER ACTIVITY LOGS</h3>
        {summary && (
          <div className="flex items-center gap-3 text-xs text-text-muted">
            <span>
              <strong className="text-text-primary">{summary.total}</strong>{' '}
              {summary.total === 1 ? 'attempt' : 'attempts'}
            </span>
            {summary.approved > 0 && (
              <span className="text-green-600">· {summary.approved} approved</span>
            )}
            {summary.rejected > 0 && (
              <span className="text-red-600">· {summary.rejected} rejected</span>
            )}
            {summary.cancelled > 0 && (
              <span className="text-orange-600">· {summary.cancelled} cancelled</span>
            )}
            {summary.dropped > 0 && (
              <span className="text-yellow-600">· {summary.dropped} dropped</span>
            )}
          </div>
        )}
      </div>

      {/* Scrollable attempt list (newest first) */}
      <div className="flex-1 min-h-0 overflow-y-auto space-y-3 pr-1">
        {logs.attempts.map((attempt, idx) => (
          <AttemptSection
            key={attempt.attempt_id}
            attempt={attempt}
            defaultOpen={idx === 0}
          />
        ))}
      </div>
    </div>
  );
};
